import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { TrendingUp } from '@mui/icons-material';
import { fmtINR, isEmptyObject, sessionExpireHandle } from '../../../util/helper';
import './Dashboard.css';
import Background from '../../common/Background';
import Cardimg1 from "../../../assets/img/illustrations/corner-1.png";
import BrokerService from '../../../services/broker.service';
import { isMarketTime, isWeekendDays, checkHoliday } from '../../../util/date-calculations';
import { getHolidays } from '../../../redux/actions/optionChain';

const ManualAlgoPnlCards = () => {
  const user = useSelector((state) => state.user);
  const [manualPnlRaw, setManualPnlRaw] = useState(0);
  const [algoPnlRaw, setAlgoPnlRaw] = useState(0);
  const [positionCount, setPositionCount] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [holidays, setHolidays] = useState([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  

  useEffect(() => {
    const loadHolidays = async () => {
        const res = await getHolidays('nse,nfo');
        if (res && res?.sessionExpired) {
          sessionExpireHandle()
        } else if (!isEmptyObject(res) && res["NFO"]) {
          setHolidays(res["NFO"]);
        }
    };
    loadHolidays();
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      const userId = user?.userID;
      if (!userId) return;
      const res = await BrokerService.getUserDashboard(userId);
      const data = res?.data || res;
      if (!data) return;
      setManualPnlRaw(Number(data?.manualPnl ?? 0));
      setAlgoPnlRaw(Number(data?.algoPnl ?? 0));
      setPositionCount(Number(data?.positionsCount ?? 0));
      setTotalOrders(Number(data?.ordersCount ?? 0));
      setLastUpdatedAt(new Date());
    };

    const todayStr = () => {
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    };

    const canPoll = () => {
      if (!isMarketTime()) return false;
      if (isWeekendDays()) return false;
      if (!holidays || holidays.length === 0) return false; 
      const isHolidayToday = checkHoliday(holidays, todayStr());
      return !isHolidayToday;
    };
    let timerId;
    const scheduleNext = () => {
      const now = new Date();
      const seconds = now.getSeconds();
      const ms = now.getMilliseconds();
      
      let delay;
      if (seconds < 5) {
        delay = (5 - seconds) * 1000 - ms;
      } else {
        delay = (60 - seconds + 5) * 1000 - ms;
      }

      if (delay < 500) delay += 60000;

      timerId = setTimeout(async () => {
        if (canPoll()) {
          await loadDashboard();
        }
        scheduleNext();
      }, delay);
    };

    loadDashboard();
    scheduleNext();

    return () => clearTimeout(timerId);
  }, [user?.userID, holidays]);

  const manualPositive = Number(manualPnlRaw) >= 0;
  const algoPositive = Number(algoPnlRaw) >= 0;

  return (
      <Background image={Cardimg1} className="bg-card">
      {lastUpdatedAt ? (
        <div className='section-subtitle' style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>
          Last updated at {lastUpdatedAt.toLocaleTimeString()}
        </div>
      ) : null}
      <div className="manual-algo-pnl-grid">
        <div className='quick-link-card'>
          <div className="quick-link-content">
            <div className="quick-link-icon">
              <TrendingUp fontSize="small" />
            </div>
            <div className="quick-link-text">
              <h3 className="quick-link-title">Manual PnL</h3>
              <p className={`pnl-amount ${manualPositive ? 'positive' : 'negative'}`}>
                {manualPositive ? '+' : ''}{fmtINR(Number(manualPnlRaw))}
              </p>
            </div>
          </div>
        </div>

        <div className={`quick-link-card`}>
          <div className="quick-link-content">
            <div className="quick-link-icon">
              <TrendingUp fontSize="small" />
            </div>
            <div className="quick-link-text">
              <h3 className="quick-link-title">Algo PnL</h3>
              <p className={`pnl-amount ${algoPositive ? 'positive' : 'negative'}`}>
                {algoPositive ? '+' : ''}{fmtINR(Number(algoPnlRaw))}
              </p>
            </div>
          </div>
        </div>

        <div className='quick-link-card'>
          <div className="quick-link-content">
            <div className="quick-link-icon">
              <TrendingUp fontSize="small" />
            </div>
            <div className="quick-link-text">
              <h3 className="quick-link-title">Positions</h3>
              <span className='position-count'>
                {positionCount}
              </span>
            </div>
          </div>
        </div>

        <div className='quick-link-card'>
          <div className="quick-link-content">
            <div className="quick-link-icon">
              <TrendingUp fontSize="small" />
            </div>
            <div className="quick-link-text">
              <h3 className="quick-link-title">Total Orders</h3>
              <span className='position-count'>
                {totalOrders}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Background>
  );
};

export default ManualAlgoPnlCards;


